// Widget size constraints - min/max col-span and row-span per widget type

import type { Bilingual } from '@/config/bilingual';

export interface WidgetConstraints {
  minColSpan: number;
  maxColSpan: number;
  minRowSpan: number;
  maxRowSpan: number;
  defaultColSpan: number;
  defaultRowSpan: number;
  resizable: boolean;
}

// FloorPlan layout uses pixel-based constraints (reference canvas 1920×1080)
export interface FloorPlanWidgetConstraints {
  minW: number;
  maxW: number;
  minH: number;
  maxH: number;
  defaultW: number;
  defaultH: number;
  resizable: boolean;
}

export type WidgetType =
  | 'location-card'
  | 'global-mode'
  | 'security-mode'
  | 'climate-mode'
  | 'energy-summary'
  | 'energy-flow'
  | 'energy-cost'
  | 'energy-schedule'
  | 'lights-summary'
  | 'lights-groups'
  | 'device-control'
  | 'device-list'
  | 'room'
  | 'room-list'
  | 'scene'
  | 'chart'
  | 'time'
  | 'weather'
  | 'camera'
  | 'camera-list'
  | 'media-player'
  | 'webpage'
  | 'site'
  | 'status'
  | 'solar'
  | 'device-icon'
  | 'intercom'
  | 'calendar'
  | 'notes'
  | 'photos'
  | 'shopping'
  | 'navigation-link'
  | 'navigation-card'
  | 'model-view';

export const WIDGET_CONSTRAINTS: Record<WidgetType, WidgetConstraints> = {
  'location-card': {
    minColSpan: 3,
    maxColSpan: 6,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'global-mode': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  'security-mode': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  'climate-mode': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  'energy-summary': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'energy-flow': {
    minColSpan: 3,
    maxColSpan: 6,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'energy-cost': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'energy-schedule': {
    minColSpan: 3,
    maxColSpan: 12,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 6,
    defaultRowSpan: 3,
    resizable: true,
  },
  'lights-summary': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'lights-groups': {
    minColSpan: 3,
    maxColSpan: 6,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 4,
    defaultRowSpan: 2,
    resizable: true,
  },
  'device-control': {
    minColSpan: 1,
    maxColSpan: 2,
    minRowSpan: 1,
    maxRowSpan: 2,
    defaultColSpan: 2,
    defaultRowSpan: 1,
    resizable: true,
  },
  'device-list': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  room: {
    minColSpan: 2,
    maxColSpan: 3,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 2,
    defaultRowSpan: 2,
    resizable: true,
  },
  'room-list': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  scene: {
    minColSpan: 1,
    maxColSpan: 2,
    minRowSpan: 1,
    maxRowSpan: 2,
    defaultColSpan: 2,
    defaultRowSpan: 1,
    resizable: true,
  },
  chart: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  time: {
    minColSpan: 1,
    maxColSpan: 4,
    minRowSpan: 1,
    maxRowSpan: 4,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    // Room to grow: the clock card carries the month calendar, the sun/moon
    // band, world clocks and the schedule, which need a bigger tile than the
    // bare clock it used to be.
    resizable: true,
  },
  weather: {
    minColSpan: 2,
    maxColSpan: 3,
    minRowSpan: 1,
    maxRowSpan: 2,
    defaultColSpan: 2,
    defaultRowSpan: 1,
    resizable: true,
  },
  camera: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  'camera-list': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 3,
    defaultRowSpan: 3,
    resizable: true,
  },
  'media-player': {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 1,
    maxRowSpan: 3,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  webpage: {
    minColSpan: 2,
    maxColSpan: 6,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 4,
    defaultRowSpan: 3,
    resizable: true,
  },
  site: {
    minColSpan: 2,
    maxColSpan: 3,
    minRowSpan: 2,
    maxRowSpan: 3,
    defaultColSpan: 2,
    defaultRowSpan: 2,
    resizable: true,
  },
  status: {
    minColSpan: 1,
    maxColSpan: 2,
    minRowSpan: 1,
    maxRowSpan: 2,
    defaultColSpan: 2,
    defaultRowSpan: 1,
    resizable: true,
  },
  solar: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 2,
    defaultRowSpan: 2,
    resizable: true,
  },
  'device-icon': {
    minColSpan: 1,
    maxColSpan: 1,
    minRowSpan: 1,
    maxRowSpan: 1,
    defaultColSpan: 1,
    defaultRowSpan: 1,
    resizable: false,
  },
  intercom: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 2,
    defaultRowSpan: 3,
    resizable: true,
  },
  calendar: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 2,
    defaultRowSpan: 3,
    resizable: true,
  },
  notes: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 2,
    defaultRowSpan: 3,
    resizable: true,
  },
  photos: {
    minColSpan: 2,
    maxColSpan: 4,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 3,
    defaultRowSpan: 3,
    resizable: true,
  },
  shopping: {
    minColSpan: 2,
    maxColSpan: 3,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 2,
    defaultRowSpan: 3,
    resizable: true,
  },
  'navigation-link': {
    minColSpan: 1,
    maxColSpan: 3,
    minRowSpan: 1,
    maxRowSpan: 2,
    defaultColSpan: 2,
    defaultRowSpan: 1,
    resizable: true,
  },
  'navigation-card': {
    minColSpan: 2,
    maxColSpan: 5,
    minRowSpan: 2,
    maxRowSpan: 4,
    defaultColSpan: 3,
    defaultRowSpan: 2,
    resizable: true,
  },
  'model-view': {
    minColSpan: 2,
    maxColSpan: 6,
    minRowSpan: 2,
    maxRowSpan: 5,
    defaultColSpan: 3,
    defaultRowSpan: 3,
    resizable: true,
  },
};

/**
 * Validate and clamp widget size to constraints
 */
export function clampWidgetSize(
  widgetType: WidgetType,
  colSpan: number,
  rowSpan: number,
): { colSpan: number; rowSpan: number } {
  const constraints = getWidgetConstraints(widgetType);

  return {
    colSpan: Math.max(constraints.minColSpan, Math.min(constraints.maxColSpan, colSpan)),
    rowSpan: Math.max(constraints.minRowSpan, Math.min(constraints.maxRowSpan, rowSpan)),
  };
}

/**
 * Check if a widget can be resized
 */
export function isWidgetResizable(widgetType: WidgetType): boolean {
  return getWidgetConstraints(widgetType).resizable;
}

/**
 * Get default size for a new widget
 */
export function getDefaultWidgetSize(widgetType: WidgetType): {
  colSpan: number;
  rowSpan: number;
} {
  const constraints = getWidgetConstraints(widgetType);
  return {
    colSpan: constraints.defaultColSpan,
    rowSpan: constraints.defaultRowSpan,
  };
}

/**
 * Widget display names for UI, in both languages.
 *
 * The value is the app's own bilingual shape (`Bilingual`, `config/bilingual.ts`):
 * a data table cannot know the reader's language, so it carries both halves and the
 * render site picks with `tt(name.en, name.sl)`. The keys are the `WidgetType` ids
 * the layouts store, and they never change.
 */
export const WIDGET_DISPLAY_NAMES: Record<WidgetType, Bilingual> = {
  'location-card': { en: 'Location Card', sl: 'Kartica lokacije' },
  'global-mode': { en: 'Modes', sl: 'Načini' },
  'security-mode': { en: 'Security Mode', sl: 'Varnostni način' },
  'climate-mode': { en: 'Climate Mode', sl: 'Klimatski način' },
  'energy-summary': { en: 'Energy Summary', sl: 'Povzetek energije' },
  'energy-flow': { en: 'Energy Flow', sl: 'Pretok energije' },
  'energy-cost': { en: 'Energy Cost', sl: 'Stroški energije' },
  'energy-schedule': { en: 'Energy Schedules', sl: 'Urniki energije' },
  'lights-summary': { en: 'Lights Summary', sl: 'Povzetek luči' },
  'lights-groups': { en: 'Lights Groups', sl: 'Skupine luči' },
  'device-control': { en: 'Device Control', sl: 'Nadzor naprave' },
  'device-list': { en: 'Devices Card', sl: 'Kartica naprav' },
  room: { en: 'Room Card', sl: 'Kartica prostora' },
  'room-list': { en: 'Rooms Card', sl: 'Kartica prostorov' },
  scene: { en: 'Scene Buttons', sl: 'Gumbi scen' },
  chart: { en: 'Chart', sl: 'Graf' },
  time: { en: 'Time & Date', sl: 'Čas in datum' },
  weather: { en: 'Weather', sl: 'Vreme' },
  camera: { en: 'Camera Feed', sl: 'Kamera v živo' },
  'camera-list': { en: 'Camera List', sl: 'Seznam kamer' },
  'media-player': { en: 'Now Playing', sl: 'Trenutno predvaja' },
  webpage: { en: 'Web Page', sl: 'Spletna stran' },
  site: { en: 'Site Card', sl: 'Kartica objekta' },
  status: { en: 'System Status', sl: 'Stanje sistema' },
  solar: { en: 'Sun & Moon', sl: 'Sonce in luna' },
  'device-icon': { en: 'Device Icon', sl: 'Ikona naprave' },
  intercom: { en: 'Intercom', sl: 'Domofon' },
  calendar: { en: 'Calendar', sl: 'Koledar' },
  notes: { en: 'Notes', sl: 'Zapiski' },
  photos: { en: 'Photos', sl: 'Fotografije' },
  shopping: { en: 'Lists', sl: 'Seznami' },
  'navigation-link': { en: 'Navigation Link', sl: 'Navigacijska povezava' },
  'navigation-card': { en: 'Navigation Card', sl: 'Navigacijska kartica' },
  'model-view': { en: 'Home Model', sl: 'Model hiše' },
};

/**
 * Widget descriptions for selection modal — same bilingual shape as the names above,
 * rendered with `tt(description.en, description.sl)`.
 */
export const WIDGET_DESCRIPTIONS: Record<WidgetType, Bilingual> = {
  'location-card': {
    en: 'Overview with time, weather, status, and quick scenes',
    sl: 'Pregled s časom, vremenom, stanjem in hitrimi scenami',
  },
  'global-mode': {
    en: 'Switch the whole space between modes — Home, Away, Night, Vacation',
    sl: 'Preklop celotnega prostora med načini — Doma, Odhod, Noč, Dopust',
  },
  'security-mode': {
    en: 'Arm, disarm, and monitor the current security mode',
    sl: 'Vklop in izklop varovanja ter spremljanje trenutnega varnostnega načina',
  },
  'climate-mode': {
    en: 'Switch between seasonal and manual climate modes',
    sl: 'Preklapljanje med sezonskim in ročnim klimatskim načinom',
  },
  'energy-summary': {
    en: "Today's key solar, grid, self-use, and cost metrics",
    sl: 'Današnje ključne meritve sonca, omrežja, samooskrbe in stroškov',
  },
  'energy-flow': {
    en: 'Live power flow between solar, home, grid, and battery',
    sl: 'Pretok moči v živo med soncem, domom, omrežjem in baterijo',
  },
  'energy-cost': {
    en: "Price bars and today's estimated energy cost",
    sl: 'Cenovne lestvice in današnji ocenjeni stroški energije',
  },
  'energy-schedule': {
    en: 'Scheduled devices prioritized for energy optimization',
    sl: 'Načrtovane naprave, prednostno razvrščene za optimizacijo energije',
  },
  'lights-summary': {
    en: 'Managed lights, active rooms, and quick all-on or all-off actions',
    sl: 'Upravljane luči, aktivni prostori in hitra dejanja vse-vklop/vse-izklop',
  },
  'lights-groups': {
    en: 'Saved light groups with quick commands and preset shortcuts',
    sl: 'Shranjene skupine luči s hitrimi ukazi in bližnjicami do prednastavitev',
  },
  'device-control': {
    en: 'Control a single device with full controls',
    sl: 'Upravljanje posamezne naprave s polnim naborom kontrolnikov',
  },
  'device-list': {
    en: 'Grid of multiple device buttons for quick access',
    sl: 'Mreža gumbov več naprav za hiter dostop',
  },
  room: {
    en: 'Display room with cover image and device overlays',
    sl: 'Prikaz prostora s sliko in prekrivnimi elementi naprav',
  },
  'room-list': { en: 'Grid of room buttons from a selected area', sl: 'Mreža gumbov prostorov iz izbranega območja' },
  scene: { en: 'Buttons to trigger scenes', sl: 'Gumbi za sprožanje scen' },
  chart: { en: 'Time-series data visualization', sl: 'Prikaz časovnih vrst podatkov' },
  time: {
    en: 'Hub-synced clock, month calendar, sun & moon rises, world clocks and schedule',
    sl: 'Z hubom sinhronizirana ura, mesečni koledar, vzhodi sonca in lune, svetovne ure in razpored',
  },
  weather: { en: 'Weather forecast and current conditions', sl: 'Vremenska napoved in trenutne razmere' },
  camera: { en: 'Live camera feed display', sl: 'Prikaz kamere v živo' },
  'camera-list': { en: 'Grid of multiple cameras with snapshots', sl: 'Mreža več kamer s posnetki' },
  'media-player': {
    en: 'Now playing card with playback controls',
    sl: 'Kartica trenutnega predvajanja z nadzorom predvajanja',
  },
  webpage: { en: 'Embed external webpage or dashboard', sl: 'Vdelana zunanja spletna stran ali nadzorna plošča' },
  site: {
    en: 'Site-wide errors, warnings, signal lights and top room status',
    sl: 'Napake in opozorila objekta, signalne luči ter stanje glavnih prostorov',
  },
  status: { en: 'System and integration status indicators', sl: 'Kazalniki stanja sistema in integracij' },
  solar: { en: 'Sunrise, sunset arc and live moon phase', sl: 'Sončni vzhod, lok zahoda in trenutna lunina faza' },
  'device-icon': {
    en: 'Compact round device icon pin for floor plans with thumbnail support',
    sl: 'Kompaktna okrogla ikona naprave za tlorise s podporo sličicam',
  },
  intercom: {
    en: 'Touch panels and connected apps with call and announce controls',
    sl: 'Dotikalne plošče in povezane aplikacije s klici in najavami',
  },
  calendar: {
    en: 'Upcoming events from your personal calendars',
    sl: 'Prihajajoči dogodki iz vaših osebnih koledarjev',
  },
  notes: {
    en: 'Recent notes with quick-add from the dashboard',
    sl: 'Nedavni zapiski s hitrim dodajanjem z nadzorne plošče',
  },
  photos: { en: 'Recent photos from your gallery', sl: 'Nedavne fotografije iz vaše galerije' },
  shopping: {
    en: 'List with item toggling and quick-add',
    sl: 'Seznam z odkljukavanjem in hitrim dodajanjem',
  },
  'navigation-link': {
    en: 'Single shortcut to a built-in page or dashboard with icon or button styling',
    sl: 'Ena bližnjica do vgrajene strani ali nadzorne plošče z ikono ali gumbom',
  },
  'navigation-card': {
    en: 'Launcher-style grid of selected pages and dashboards',
    sl: 'Mreža izbranih strani in nadzornih plošč v slogu zaganjalnika',
  },
  'model-view': {
    en: 'The home model in a saved view — a still picture, click to explore',
    sl: 'Model hiše v shranjenem pogledu — mirujoča slika, klik za raziskovanje',
  },
};

/**
 * Widget icons (using emoji for now, can be replaced with icon library)
 */
export const WIDGET_ICONS: Record<WidgetType, string> = {
  'location-card': '📍',
  'global-mode': '🏠',
  'security-mode': '🛡️',
  'climate-mode': '🌡️',
  'energy-summary': '⚡',
  'energy-flow': '🔌',
  'energy-cost': '💶',
  'energy-schedule': '📅',
  'lights-summary': '💡',
  'lights-groups': '🧩',
  'device-control': '🎛️',
  'device-list': '📋',
  room: '🏠',
  'room-list': '🏘️',
  scene: '🎬',
  chart: '📊',
  time: '🕐',
  weather: '🌤️',
  camera: '📹',
  'camera-list': '📷',
  'media-player': '🎵',
  webpage: '🌐',
  site: '🚨',
  status: '📡',
  solar: '🌅',
  'device-icon': '🔵',
  intercom: '🔔',
  calendar: '📅',
  notes: '📝',
  photos: '🖼️',
  shopping: '🛒',
  'navigation-link': '🔗',
  'navigation-card': '🧭',
  'model-view': '🏡',
};

/**
 * FloorPlan layout constraints — pixel-based on a 1920×1080 reference canvas.
/**
 * The square a device widget starts at, px — the device-icon tile on a plan or a
 * BIM scene, and the box a SCADA device node is placed in. One number so a
 * device reads the same wherever it is placed.
 */
export const DEVICE_WIDGET_TILE = 75;

/**
 * Scale proportionally for other aspect ratios (values designed for widescreen).
 */
export const FLOORPLAN_WIDGET_CONSTRAINTS: Record<WidgetType, FloorPlanWidgetConstraints> = {
  'location-card': { minW: 288, maxW: 960, minH: 162, maxH: 432, defaultW: 480, defaultH: 216, resizable: true },
  'global-mode': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'security-mode': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'climate-mode': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'energy-summary': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'energy-flow': { minW: 384, maxW: 960, minH: 162, maxH: 432, defaultW: 576, defaultH: 270, resizable: true },
  'energy-cost': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'energy-schedule': { minW: 384, maxW: 1200, minH: 216, maxH: 540, defaultW: 768, defaultH: 324, resizable: true },
  'lights-summary': { minW: 288, maxW: 768, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'lights-groups': { minW: 384, maxW: 960, minH: 162, maxH: 432, defaultW: 422, defaultH: 270, resizable: true },
  'device-control': { minW: 154, maxW: 384, minH: 86, maxH: 216, defaultW: 230, defaultH: 130, resizable: true },
  'device-list': { minW: 288, maxW: 768, minH: 162, maxH: 540, defaultW: 384, defaultH: 270, resizable: true },
  room: { minW: 230, maxW: 576, minH: 130, maxH: 324, defaultW: 346, defaultH: 194, resizable: true },
  'room-list': { minW: 288, maxW: 768, minH: 162, maxH: 540, defaultW: 422, defaultH: 302, resizable: true },
  scene: { minW: 192, maxW: 480, minH: 86, maxH: 216, defaultW: 288, defaultH: 130, resizable: true },
  chart: { minW: 384, maxW: 1152, minH: 162, maxH: 540, defaultW: 576, defaultH: 270, resizable: true },
  time: { minW: 192, maxW: 1152, minH: 86, maxH: 648, defaultW: 288, defaultH: 108, resizable: true },
  weather: { minW: 288, maxW: 672, minH: 130, maxH: 324, defaultW: 422, defaultH: 194, resizable: true },
  camera: { minW: 384, maxW: 1344, minH: 162, maxH: 648, defaultW: 576, defaultH: 238, resizable: true },
  'camera-list': { minW: 480, maxW: 1536, minH: 216, maxH: 756, defaultW: 768, defaultH: 378, resizable: true },
  'media-player': { minW: 384, maxW: 1152, minH: 130, maxH: 378, defaultW: 576, defaultH: 194, resizable: true },
  webpage: { minW: 384, maxW: 1536, minH: 216, maxH: 864, defaultW: 768, defaultH: 378, resizable: true },
  site: { minW: 346, maxW: 768, minH: 216, maxH: 540, defaultW: 422, defaultH: 302, resizable: true },
  status: { minW: 230, maxW: 576, minH: 108, maxH: 270, defaultW: 346, defaultH: 162, resizable: true },
  solar: { minW: 288, maxW: 768, minH: 162, maxH: 378, defaultW: 422, defaultH: 238, resizable: true },
  'device-icon': {
    minW: 58,
    maxW: 192,
    minH: 58,
    maxH: 192,
    defaultW: DEVICE_WIDGET_TILE,
    defaultH: DEVICE_WIDGET_TILE,
    resizable: true,
  },
  intercom: { minW: 288, maxW: 768, minH: 216, maxH: 540, defaultW: 384, defaultH: 378, resizable: true },
  calendar: { minW: 288, maxW: 768, minH: 216, maxH: 540, defaultW: 384, defaultH: 378, resizable: true },
  notes: { minW: 288, maxW: 768, minH: 216, maxH: 540, defaultW: 384, defaultH: 378, resizable: true },
  photos: { minW: 288, maxW: 960, minH: 216, maxH: 540, defaultW: 480, defaultH: 378, resizable: true },
  shopping: { minW: 288, maxW: 576, minH: 216, maxH: 540, defaultW: 384, defaultH: 378, resizable: true },
  'navigation-link': { minW: 192, maxW: 576, minH: 86, maxH: 216, defaultW: 288, defaultH: 130, resizable: true },
  'navigation-card': { minW: 288, maxW: 960, minH: 216, maxH: 648, defaultW: 480, defaultH: 302, resizable: true },
  'model-view': { minW: 384, maxW: 1536, minH: 216, maxH: 864, defaultW: 768, defaultH: 432, resizable: true },
};

/**
 * Get widget constraints for a specific layout type
 */
export function getWidgetConstraints(
  widgetType: WidgetType,
  layoutType: 'floorplan' | 'bim' | 'scada',
): FloorPlanWidgetConstraints;
export function getWidgetConstraints(widgetType: WidgetType, layoutType?: 'grid' | 'column'): WidgetConstraints;
export function getWidgetConstraints(
  widgetType: WidgetType,
  layoutType: 'grid' | 'column' | 'layout' | 'floorplan' | 'bim' | 'scada' = 'grid',
): WidgetConstraints | FloorPlanWidgetConstraints {
  // A canvas (any base) positions in canvas pixels; a page positions in cells.
  if (
    layoutType === 'layout' ||
    layoutType === 'floorplan' ||
    layoutType === 'bim' ||
    layoutType === 'scada'
  ) {
    return FLOORPLAN_WIDGET_CONSTRAINTS[widgetType];
  }
  return WIDGET_CONSTRAINTS[widgetType];
}
